import React, { useEffect, useRef } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, Animated, Platform } from 'react-native';
import { useCharacterPopup, CHARACTER_IMAGES } from '../context/CharacterPopupContext';
import { useTheme } from '../context/ThemeContext';
import { Ionicons } from '@expo/vector-icons';

// Individual popup component
const PopupItem = ({ popup, onDismiss, index }) => {
  const { palette, mode } = useTheme();
  const slideAnim = useRef(new Animated.Value(120)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.85)).current;

  useEffect(() => {
    // Entrance animation
    Animated.parallel([
      Animated.spring(slideAnim, {
        toValue: 0,
        tension: 60,
        friction: 10,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 60,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const handleDismiss = () => {
    // Exit animation
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: 120,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onDismiss(popup.id);
    });
  };

  const characterImage = CHARACTER_IMAGES[popup.pose] || CHARACTER_IMAGES.smiling;
  
  // Position styles based on popup.position - increased spacing for larger cards
  const getPositionStyle = () => {
    const stackOffset = index * 220; // More space between stacked popups
    switch (popup.position) {
      case 'bottom-left':
        return { bottom: 28 + stackOffset, left: 28 };
      case 'top-right':
        return { top: 120 + stackOffset, right: 28 };
      case 'top-left':
        return { top: 120 + stackOffset, left: 28 };
      case 'bottom-right':
      default:
        return { bottom: 28 + stackOffset, right: 28 };
    }
  };

  // Get accent color and background color based on type
  const getColors = () => {
    switch (popup.type) {
      case 'success':
      case 'celebration':
      case 'achievement':
        return { accent: '#22C55E', bg: '#ECFDF5' };
      case 'warning':
      case 'error':
        return { accent: '#EF4444', bg: '#FEF2F2' };
      case 'reminder':
      case 'time':
      case 'deadline':
        return { accent: '#F59E0B', bg: '#FFFBEB' };
      case 'tip':
      case 'suggestion':
      case 'idea':
        return { accent: '#3B82F6', bg: '#EFF6FF' };
      case 'welcome':
      case 'greeting':
        return { accent: '#FF4747', bg: '#FFF5F5' };
      case 'encouragement':
      case 'motivate':
        return { accent: '#8B5CF6', bg: '#F5F3FF' };
      default:
        return { accent: '#FF4747', bg: '#FFFFFF' };
    }
  };

  const colors = getColors();

  return (
    <Animated.View
      style={[
        styles.popupContainer,
        getPositionStyle(),
        {
          opacity: opacityAnim,
          transform: [
            { translateY: slideAnim },
            { scale: scaleAnim },
          ],
        },
      ]}
    >
      <View style={[
        styles.popupCard,
        {
          backgroundColor: colors.bg,
          borderColor: '#000000',
        }
      ]}>
        {/* Colored accent bar at top */}
        <View style={[styles.accentBar, { backgroundColor: colors.accent }]} />
        
        {/* Character image - Much larger */}
        <View style={styles.characterContainer}>
          <Image 
            source={characterImage}
            style={styles.characterImage}
            resizeMode="contain"
          />
        </View>
        
        {/* Content */}
        <View style={styles.contentContainer}>
          {popup.title && (
            <Text style={styles.title}>
              {popup.title}
            </Text>
          )}
          <Text style={styles.message}>
            {popup.message}
          </Text>
        </View>
        
        {/* Close button */}
        <TouchableOpacity 
          style={[styles.closeButton, { backgroundColor: colors.accent }]}
          onPress={handleDismiss}
          activeOpacity={0.8}
        >
          <Ionicons 
            name="close" 
            size={16} 
            color="#FFFFFF" 
          />
        </TouchableOpacity>
        
        {/* Progress bar for auto-dismiss */}
        {popup.duration > 0 && (
          <ProgressBar duration={popup.duration} accentColor={colors.accent} />
        )}
      </View>
    </Animated.View>
  );
};

// Progress bar component
const ProgressBar = ({ duration, accentColor }) => {
  const progressAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 0,
      duration: duration,
      useNativeDriver: false,
    }).start();
  }, [duration]);

  return (
    <View style={styles.progressBarContainer}>
      <Animated.View
        style={[
          styles.progressBar,
          {
            backgroundColor: accentColor,
            width: progressAnim.interpolate({
              inputRange: [0, 1],
              outputRange: ['0%', '100%'],
            }),
          },
        ]}
      />
    </View>
  );
};

// Main container that renders all popups
const CharacterPopupContainer = () => {
  const { popups, dismissPopup } = useCharacterPopup();

  if (popups.length === 0) return null;

  // Group popups by position
  const groupedPopups = popups.reduce((acc, popup) => {
    const position = popup.position || 'bottom-right';
    if (!acc[position]) acc[position] = [];
    acc[position].push(popup);
    return acc;
  }, {});

  return (
    <View style={styles.container} pointerEvents="box-none">
      {Object.entries(groupedPopups).map(([position, positionPopups]) =>
        positionPopups.map((popup, index) => (
          <PopupItem
            key={popup.id}
            popup={popup}
            onDismiss={dismissPopup}
            index={index}
          />
        ))
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...Platform.select({
      web: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }
    }),
  },
  popupContainer: {
    position: 'absolute',
    zIndex: 10000,
    ...Platform.select({
      web: {
        pointerEvents: 'auto',
      }
    }),
  },
  popupCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    borderWidth: 4,
    borderColor: '#000000',
    overflow: 'hidden',
    maxWidth: 440,
    minWidth: 380,
    minHeight: 140,
    ...Platform.select({
      web: {
        boxShadow: '6px 8px 0px #000000',
      }
    }),
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
  },
  characterContainer: {
    width: 130,
    height: 160,
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginLeft: -10,
    marginBottom: -8,
  },
  characterImage: {
    width: 140,
    height: 170,
  },
  contentContainer: {
    flex: 1,
    paddingVertical: 20,
    paddingRight: 48,
    paddingLeft: 0,
    paddingTop: 28,
  },
  title: {
    fontSize: 17,
    fontWeight: '800',
    marginBottom: 6,
    fontFamily: 'Plus Jakarta Sans',
    color: '#000000',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.1)',
      }
    }),
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '500',
    color: '#4B5563',
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '2px 2px 0px #000000',
      }
    }),
  },
  progressBarContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 5,
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  progressBar: {
    height: '100%',
  },
});

export default CharacterPopupContainer;
